import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Urls, Titles } from '@/lib/navigation';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useIsMobileDevice } from '@/hooks/useIsMobileDevice';
import { trackDemoLaunched } from '@/analytics';
import { useOptionalVmClient, useVmConnection } from '@/api/context';
import { useAppSession } from '@/contexts/SessionContext';
import type { DemoManifestEntry } from '@/api/types';
import { usePaperThemeActive } from '@/hooks/useThemeFlag';
import { PaperDemosPage } from '@/components/paper/PaperDemosPage';

// Demos with `mobile_order` come first (sorted ascending); demos without
// it preserve their server order at the end.
const sortDemosForMobile = (demos: DemoManifestEntry[]): DemoManifestEntry[] => {
  const ordered: DemoManifestEntry[] = [];
  const unordered: DemoManifestEntry[] = [];
  for (const demo of demos) {
    if (demo.mobile_order != null) ordered.push(demo);
    else unordered.push(demo);
  }
  ordered.sort((a, b) => a.mobile_order! - b.mobile_order!);
  return [...ordered, ...unordered];
};

const PLACEHOLDER_GRADIENTS = [
  'from-violet-600/20 via-fuchsia-600/10 to-cyan-600/20',
  'from-rose-600/20 via-orange-600/10 to-amber-600/20',
  'from-emerald-600/20 via-teal-600/10 to-cyan-600/20',
  'from-blue-600/20 via-indigo-600/10 to-purple-600/20',
  'from-pink-600/20 via-rose-600/10 to-red-600/20',
  'from-cyan-600/20 via-sky-600/10 to-blue-600/20',
];

const DemoTile = memo(function DemoTile({
  demo,
  index,
}: {
  demo: DemoManifestEntry;
  index: number;
}) {
  const vmClient = useOptionalVmClient();
  const { actions } = useAppSession();
  const [imgFailed, setImgFailed] = useState(false);
  const [activating, setActivating] = useState(false);
  const [hover, setHover] = useState(false);

  const handleClick = useCallback(async () => {
    if (!vmClient || activating) return;
    setActivating(true);
    trackDemoLaunched(demo.name);
    try {
      const sessionId = await vmClient.activateDemo(demo.name);
      await actions.handleSelectAgent(sessionId);
    } catch {
      setActivating(false);
    }
  }, [vmClient, demo.name, actions, activating]);

  if (!vmClient) return null;

  const thumbnailSrc = demo.thumbnail_url
    ? vmClient.getThumbnailUrl(demo.thumbnail_url)
    : null;
  const showImage = thumbnailSrc && !imgFailed;
  const gradient = PLACEHOLDER_GRADIENTS[index % PLACEHOLDER_GRADIENTS.length];

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={activating}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setHover(true)}
      onBlur={() => setHover(false)}
      className={cn(
        'group relative flex flex-col overflow-hidden rounded-[10px] text-left',
        'bg-zinc-900 border border-white/10',
        'transition-all duration-200 ease-[cubic-bezier(.22,.61,.36,1)]',
        hover && !activating && 'border-[#7678ED] -translate-y-0.5 shadow-[0_12px_28px_rgba(20,18,14,0.10)]',
        activating && 'border-glow-busy pointer-events-none',
      )}
      style={activating ? { '--border-glow-fill': '#18181b' } as React.CSSProperties : undefined}
    >
      {/* Artwork / Thumbnail */}
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        {showImage ? (
          <img
            src={thumbnailSrc}
            alt={demo.title}
            onError={() => setImgFailed(true)}
            className="h-full w-full object-cover object-top"
          />
        ) : (
          <div
            className={cn(
              'flex h-full w-full items-center justify-center bg-gradient-to-br',
              gradient,
            )}
          >
            <Sparkles className="h-8 w-8 text-white/20" />
          </div>
        )}

        {/* Sliding description overlay — from bottom */}
        {demo.description && (
          <div
            className={cn(
              'absolute inset-x-0 bottom-0 px-[18px] pb-4 pt-5',
              'transition-transform duration-[320ms] ease-[cubic-bezier(.22,.61,.36,1)]',
              hover ? 'translate-y-0' : 'translate-y-[101%]',
            )}
            style={{
              background:
                'linear-gradient(to top, rgba(10,10,10,0.92) 0%, rgba(10,10,10,0.86) 60%, rgba(10,10,10,0) 100%)',
            }}
          >
            <p className="text-[12.5px] leading-[1.55] text-[rgba(255,253,249,0.92)]">
              {demo.description}
            </p>
          </div>
        )}
      </div>

      {/* Footer — title + eyebrow + dot */}
      <div className="flex items-baseline justify-between gap-2.5 border-t border-white/10 bg-zinc-900 px-4 py-3.5">
        <div className="min-w-0">
          <div className="truncate text-[17px] leading-[1.15] tracking-[-0.01em] text-zinc-100">
            {demo.title}
          </div>
          {demo.tags && demo.tags.length > 0 && (
            <div className="mt-1 truncate text-[10px] uppercase tracking-[0.14em] text-zinc-500">
              {demo.tags.join(' · ')}
            </div>
          )}
        </div>
      </div>
    </button>
  );
});

const DemoTileSkeleton = memo(function DemoTileSkeleton() {
  return (
    <div className="animate-pulse overflow-hidden rounded-[10px] border border-white/10 bg-zinc-900">
      <div className="aspect-[16/10] w-full bg-white/[0.04]" />
      <div className="flex items-baseline justify-between gap-2.5 border-t border-white/10 px-4 py-3.5">
        <div className="min-w-0 flex-1">
          <div className="h-[20px] w-3/4 rounded bg-white/[0.06]" />
          <div className="mt-1.5 h-[10px] w-1/2 rounded bg-white/[0.04]" />
        </div>
        <div className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white/[0.06]" />
      </div>
    </div>
  );
});

export const DemosPage = () => {
  const isPaper = usePaperThemeActive();
  if (isPaper) return <PaperDemosPage />;
  return <DemosPageLegacy />;
};

const DemosPageLegacy = () => {
  useDocumentTitle(Titles.demos());
  const navigate = useNavigate();
  const vmClient = useOptionalVmClient();
  const vmConnection = useVmConnection();
  const isMobileDevice = useIsMobileDevice();
  const [demos, setDemos] = useState<DemoManifestEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const displayedDemos = useMemo(
    () => (isMobileDevice ? sortDemosForMobile(demos) : demos),
    [demos, isMobileDevice],
  );

  useEffect(() => {
    if (!vmClient) {
      setDemos([]);
      setLoading(vmConnection.status === 'connecting');
      return;
    }

    let cancelled = false;
    setLoading(true);
    vmClient
      .fetchDemoManifest()
      .then((manifest) => {
        if (!cancelled) setDemos(manifest);
      })
      .catch(() => {
        if (!cancelled) setDemos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [vmClient, vmConnection.status]);

  return (
    <main className="relative flex flex-1 flex-col overflow-y-auto">
      {/* Top bar */}
      <div className="flex h-14 shrink-0 items-center justify-between px-6">
        <button
          type="button"
          onClick={() => navigate(Urls.home())}
          className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-[13px] text-zinc-400 transition-colors hover:bg-white/[0.06] hover:text-zinc-200"
        >
          <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.8} />
          Back to chat
        </button>
      </div>

      {/* Content — padded past the floating nav pill (left-6 + ~48px wide) */}
      <div
        className="flex-1 overflow-auto px-7 pt-4 pb-9 desktop:px-24"
      >
        {/* Header */}
        <div className="mb-7">
          <h1 className="text-[clamp(32px,4.4vw,48px)] font-semibold leading-[1.05] tracking-[-0.018em] text-zinc-100">
            Get started in one click
          </h1>
        </div>

        {/* 3×2 grid */}
        <div className="grid grid-cols-1 gap-5 desktop:grid-cols-3">
          {loading
            ? Array.from({ length: 6 }).map((_, i) => <DemoTileSkeleton key={i} />)
            : displayedDemos.map((demo, i) => (
                <DemoTile key={demo.id} demo={demo} index={i} />
              ))}
        </div>

      </div>
    </main>
  );
};
